/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { LearnSectionFragment } from "@cosmos/hygraph-schema";

import {
	ResponsiveContainer,
	SectionHeading,
	BackgroundContainer,
	Categories,
	CategoryProps,
	SectionContainer,
	CosmosTabs as Tabs,
	useTabActiveIndex,
} from "@components/elements";
import { VStack } from "@panda/jsx";
import { css } from "@panda/css";

export function LearnSection({
	config,
	categories,
	tabs = [],
	applicationFamily,
}: LearnSectionFragment): React.JSX.Element | null {
	const [activeIndex, setActiveIndex] = useTabActiveIndex(0);

	// Derive string value for Categories component
	const activeTabValue = tabs[activeIndex]?.value || tabs[0]?.value || "";

	const validHeading = Boolean(config?.heading?.title || config?.heading?.desc);
	const validContent = Boolean(categories?.length || tabs?.length);
	const enableTabs = tabs.length > 1;

	if (!validHeading && !validContent) return null;
	return (
		<BackgroundContainer
			bgAnimation={config?.backgroundAnimation}
			bgImageAsset={config?.backgroundImage}
			bgFullHeight={config?.backgroundFullHeight}
			edsBackgroundColor={config?.edsBackgroundColor}
		>
			<ResponsiveContainer>
				<SectionContainer disablePaddingBlockEnd={!!config?.disablePaddingBottom}>
					<VStack alignItems="stretch" gap="eds.dimension.24" className="hydrus-column-list-section">
						{validHeading ? <SectionHeading {...config?.heading} /> : null}
						{validContent ? (
							<VStack
								alignItems="stretch"
								gap="eds.dimension.48"
								className={css({ lgDown: { gap: "eds.dimension.40" } })}
							>
								{enableTabs ? (
									<Tabs
										activeIndex={activeIndex}
										onActiveIndexChange={(val) => val !== undefined && setActiveIndex(val)}
									>
										<Tabs.ButtonList gradientColor={config?.edsBackgroundColor ?? undefined}>
											{tabs.map((tabItem, index) => (
												<Tabs.Button
													key={tabItem.value}
													index={index}
													label={tabItem.label ?? ""}
													analytics={{
														eventCategory: "button",
														interactionType: "click",
														eventAction: "select",
														eventLabel: tabItem.label ?? null,
														eventValue: {
															component: "LearnSection",
															tabValue: tabItem.value,
														},
													}}
												/>
											))}
										</Tabs.ButtonList>
									</Tabs>
								) : null}
								<Categories
									applicationFamily={applicationFamily}
									activeTab={activeTabValue}
									categories={categories as CategoryProps[]}
								/>
							</VStack>
						) : null}
					</VStack>
				</SectionContainer>
			</ResponsiveContainer>
		</BackgroundContainer>
	);
}
